import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { track } from "@/lib/analytics";
import { resolveRoleCta, buildAuthPath } from "@/lib/roleCta";
import { normalizeHomePageData, selectHomePricePreview } from "@/lib/homePageData";
// Premium agricultural charts are preserved in code but hidden during free launch.
const SHOW_PREMIUM_MARKET_DATA = false;
import WrongRoleModal from "@/components/WrongRoleModal";
import ProductCard from "@/components/ProductCard";
import { VerifiedBadge } from "@/components/Badges";
import {
    MagnifyingGlass, Handshake, Truck, ArrowRight,
    Cow, Orange, Carrot, Grains, Plant, Tractor, MapPin,
    Star, Package, Storefront, Egg, Cheese, Hamburger,
    Warning, ShoppingCart, Fish, Bird, BowlFood, Flower, TreeEvergreen, Coffee, Acorn, Hexagon,
} from "@phosphor-icons/react";
import UrgencyBadge from "@/components/UrgencyBadge";

const CAT_ICONS = {
    ganaderia: Cow, frutas: Orange, verduras: Carrot, granos: Grains,
    forrajes: Plant, suministros: Tractor, lacteos: Cheese, huevos_miel: Egg, carnes: Hamburger,
    "granos-cereales": Grains, leguminosas: Grains, nueces: Acorn, hongos: BowlFood,
    ganado: Cow, aves: Bird, "pesca-acuicultura": Fish, "semillas-plantas": Plant,
    insumos: Package, "miel-apicola": Hexagon, "cultivos-especiales": Coffee,
    flores: Flower, procesados: Storefront, forestales: TreeEvergreen,
    "servicios-agricolas": Handshake, maquinaria: Tractor, otros: Package,
};

const CAT_DESCRIPTIONS = {
    frutas: "Cosechas frescas y de temporada", verduras: "Del cultivo a tu negocio",
    "granos-cereales": "Maíz, trigo y más cosechas", leguminosas: "Frijol, lenteja y garbanzo",
    nueces: "Nueces y semillas comestibles", forrajes: "Alimento para tu ganado",
    hongos: "Variedades de hongos comestibles", ganado: "Ganado y animales de granja",
    ganaderia: "Ganado y animales de granja", aves: "Aves, huevo y derivados",
    "pesca-acuicultura": "Productos de pesca y cultivo", lacteos: "Leche, quesos y derivados",
    "semillas-plantas": "Todo para comenzar a cultivar", insumos: "Lo que tu cultivo necesita",
    "miel-apicola": "Miel y productos de la colmena", "cultivos-especiales": "Café, cacao y especialidades",
    flores: "Flores, plantas y ornamentales", procesados: "Productos elaborados del campo",
    forestales: "Madera y productos forestales", "servicios-agricolas": "Apoyo para el trabajo del campo",
    maquinaria: "Equipo para tu operación", otros: "Más productos del campo",
};

export default function HomePage() {
    const [categories, setCategories] = useState([]);
    const [featured, setFeatured] = useState([]);
    const [newest, setNewest] = useState([]);
    const [urgentReqs, setUrgentReqs] = useState([]);
    const [transportRequests, setTransportRequests] = useState([]);
    const [pricePromo, setPricePromo] = useState([]);
    const [topProducers, setTopProducers] = useState([]);
    const [loadState, setLoadState] = useState({
        categories: "loading",
        marketplace: "loading",
        marketPrices: "loading",
    });
    const [q, setQ] = useState("");
    const [showAllCategories, setShowAllCategories] = useState(false);
    const [wrongRole, setWrongRole] = useState(null); // {currentRole, requiredRole}
    const { user } = useAuth();
    const navigate = useNavigate();
    useEffect(() => {
        let cancelled = false;
        // The mobile homepage must not wait for six independent APIs to finish.
        // Render each section as soon as its own request resolves.
        const request = (name, path, params, onData, onError) => {
            api.get(path, params).then(({ data }) => {
                if (!cancelled) onData(data);
            }).catch(() => {
                if (cancelled) return;
                console.warn(`HomePage request unavailable: ${name}`);
                onError?.();
            });
        };
        request("categories", "/meta/categories", undefined, (raw) => {
            const data = normalizeHomePageData({ categories: raw });
            setCategories(data.categories);
            setLoadState((prev) => ({ ...prev, categories: data.available.categories ? "loaded" : "unavailable" }));
        }, () => setLoadState((prev) => ({ ...prev, categories: "unavailable" })));
        const availability = { featured: false, newest: false, featuredCount: 0, newestCount: 0, finished: 0 };
        const consumeListings = (kind, raw, failed = false) => {
            const items = normalizeHomePageData({ [kind]: raw })[kind];
            if (kind === "featured") setFeatured(items);
            else setNewest(items);
            availability[kind] = !failed && Array.isArray(raw?.items);
            availability[kind + "Count"] = items.length;
            availability.finished += 1;
            if (availability.finished !== 2) return;

            // Some installations have real /products listings but empty featured/newest
            // indexes. Fall back to the same public endpoint as Explore, not demo data.
            if (!availability.featuredCount && !availability.newestCount) {
                request("public listings fallback", "/products", { params: { sort: "recent", limit: 8 } },
                    (fallback) => {
                        setNewest(normalizeHomePageData({ newest: fallback }).newest);
                        setLoadState((prev) => ({ ...prev, marketplace: "loaded" }));
                    },
                    () => setLoadState((prev) => ({ ...prev, marketplace: "unavailable" })));
            } else {
                setLoadState((prev) => ({ ...prev, marketplace: "loaded" }));
            }
        };
        request("featured", "/products/featured", { params: { limit: 8 } },
            (raw) => consumeListings("featured", raw),
            () => consumeListings("featured", null, true));
        request("newest", "/products/newest", { params: { limit: 8 } },
            (raw) => consumeListings("newest", raw),
            () => consumeListings("newest", null, true));
        request("transport requests", "/buyer-requests", { params: { limit: 60 } },
            (raw) => setTransportRequests((raw.items || []).filter((r) => r.delivery_required).slice(0, 3)),
            () => setTransportRequests([]));
        request("urgent requests", "/buyer-requests/urgent", { params: { limit: 6 } },
            (raw) => setUrgentReqs(normalizeHomePageData({ urgentRequests: raw }).urgentRequests));
        request("top producers", "/directory/top/producers", { params: { limit: 6 } },
            (raw) => setTopProducers(normalizeHomePageData({ topProducers: raw }).topProducers));
        if (SHOW_PREMIUM_MARKET_DATA) request("market prices", "/market-prices/preview", undefined, (raw) => {
            const prices = normalizeHomePageData({ marketProducts: raw });
            setPricePromo(selectHomePricePreview(prices.marketProducts));
            setLoadState((prev) => ({ ...prev, marketPrices: prices.available.marketProducts ? "loaded" : "unavailable" }));
        }, () => setLoadState((prev) => ({ ...prev, marketPrices: "unavailable" })));
        return () => { cancelled = true; };
    }, []);

    const isEmptyMarketplace = (
        loadState.marketplace === "loaded"
        && featured.length === 0
        && newest.length === 0
    );

    // Central handler for the three role CTAs (Vender / Comprar / Recibir cargas).
    const handleRoleCta = (opts) => {
        const { role, redirect, allowGuest, analyticsEvent } = opts;
        // Analytics — fire and forget, never blocks navigation.
        track(analyticsEvent, {
            source: "homepage_hero",
            selected_role: role,
            current_role: user?.role || "guest",
            required_role: role,
            redirect_path: redirect,
        });
        const decision = resolveRoleCta({ role, redirect, user, allowGuest, action: role === "productor" ? "publicar" : undefined });
        if (decision.action === "wrong-role") {
            setWrongRole({ currentRole: decision.currentRole, requiredRole: decision.requiredRole });
            return;
        }
        navigate(decision.to);
    };

    return (
        <>
            {/* Hero */}
            <section className="relative overflow-hidden bg-white" data-testid="hero-section">
                <div className="container-page grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 py-5 sm:py-10 lg:py-12 relative">
                    <div className="lg:col-span-8 relative z-10">
                        <div className="eyebrow mb-3">🌾 Marketplace agrícola nacional</div>
                        <h1 className="text-2xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.08] max-w-3xl" data-testid="hero-title">
                            Compra, vende y encuentra transporte
                        </h1>
                        <p className="mt-4 text-base sm:text-lg text-muted-foreground max-w-2xl leading-relaxed" data-testid="hero-subtitle">
                            Productos, solicitudes de compra y fletes en un solo lugar. Explora gratis; crea tu cuenta para contactar o publicar.
                        </p>

                        {/* Primary role CTAs */}
                        <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 max-w-2xl" data-testid="hero-role-ctas">
                            <button
                                type="button"
                                onClick={() => { track("homepage_sell_clicked", {source:"homepage_hero"}); navigate("/publicar?tipo=producto"); }}
                                className="btn-primary min-h-11 justify-center px-3"
                                data-testid="cta-sell"
                            >
                                <Storefront size={18} weight="fill" /> Vender
                            </button>
                            <button
                                type="button"
                                onClick={() => handleRoleCta({
                                    role: "comprador",
                                    redirect: "/marketplace",
                                    allowGuest: true,
                                    analyticsEvent: "homepage_buy_clicked",
                                })}
                                className="btn-primary min-h-11 justify-center bg-[#E8A33A] hover:bg-[#d99024] px-3"
                                data-testid="cta-buy"
                            >
                                <ShoppingCart size={18} weight="fill" /> Comprar
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    track("homepage_transport_clicked", { source: "homepage_hero", current_role: user?.role || "guest" });
                                    navigate("/ofrecer-transporte");
                                }}
                                className="btn-outline min-h-11 justify-center border-2 px-3"
                                data-testid="cta-transport"
                            >
                                <Truck size={18} weight="fill" /> Ofrezco flete
                            </button>
                            <Link to="/publicar?tipo=transporte" className="btn-outline min-h-11 justify-center px-2 text-xs sm:text-sm" data-testid="cta-need-transport">
                                <Truck size={18} /> Busco flete
                            </Link>
                        </div>

                        {/* Secondary search */}
                        <form onSubmit={(e) => { e.preventDefault(); navigate(`/marketplace?q=${encodeURIComponent(q)}`); }}
                            className="mt-5 max-w-xl flex gap-2 p-1.5 bg-white rounded-full border-2 border-border shadow-sm hover:border-[#2E7D32]/50" data-testid="hero-search-form">
                            <MagnifyingGlass size={20} className="ml-3 self-center text-muted-foreground shrink-0" />
                            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="¿Qué busca hoy? Alfalfa, becerros Angus, queso, aguacate…"
                                className="flex-1 h-11 bg-transparent focus:outline-none text-base" data-testid="hero-search-input" />
                            <button className="btn-primary h-11 px-6" data-testid="hero-search-submit">Buscar</button>
                        </form>

                        <div className="hidden sm:flex mt-5 flex-wrap gap-4 gap-y-2 text-sm text-muted-foreground">
                            <div className="inline-flex items-center gap-2"><Storefront size={18} className="text-[#2E7D32]" weight="fill" /> Vendedores registrados</div>
                            <div className="inline-flex items-center gap-2"><Handshake size={18} className="text-[#2E7D32]" weight="fill" /> Sin intermediarios</div>
                            <div className="inline-flex items-center gap-2"><Truck size={18} className="text-[#2E7D32]" weight="fill" /> Contacta transportistas</div>
                        </div>
                    </div>

                    <div className="lg:col-span-4 relative hidden sm:block">
                        <div className="rounded-3xl border border-[#2E7D32]/20 bg-[#F3F8F1] p-5 lg:h-full flex flex-col justify-center" role="img" aria-label="Vista previa del mercado agrícola en CampoDirecto">
                            <div className="rounded-2xl bg-white border border-border shadow-sm p-4">
                                <div className="flex items-center justify-between gap-3">
                                    <div>
                                        <div className="text-xs uppercase tracking-wider font-bold text-[#2E7D32]">Mercado agrícola</div>
                                        <div className="font-display font-bold text-lg">Información para decidir</div>
                                    </div>
                                    <span className="w-10 h-10 rounded-xl bg-[#2E7D32] text-white grid place-items-center"><Plant size={22} weight="fill" /></span>
                                </div>
                                <div className="mt-4 grid grid-cols-2 gap-3">
                                    <div className="rounded-xl bg-[#F7F7F5] p-3">
                                        <div className="text-xs text-muted-foreground">Publicar</div>
                                        <div className="font-bold mt-1">Gratis</div>
                                        <div className="h-1.5 rounded-full bg-[#2E7D32]/25 mt-3"><div className="h-full w-3/4 rounded-full bg-[#2E7D32]" /></div>
                                    </div>
                                    <div className="rounded-xl bg-[#F7F7F5] p-3">
                                        <div className="text-xs text-muted-foreground">Compra directa</div>
                                        <div className="font-bold mt-1">Marketplace</div>
                                        <div className="h-1.5 rounded-full bg-[#E8A33A]/25 mt-3"><div className="h-full w-2/3 rounded-full bg-[#E8A33A]" /></div>
                                    </div>
                                </div>
                                <div className="mt-3 rounded-xl border border-border p-3 flex items-center gap-3">
                                    <Truck size={24} className="text-[#2E7D32] shrink-0" weight="duotone" />
                                    <div><div className="font-semibold text-sm">Transporte conectado</div><div className="text-xs text-muted-foreground">Cargas y rutas en un solo flujo</div></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <WrongRoleModal
                open={!!wrongRole}
                onClose={() => setWrongRole(null)}
                currentRole={wrongRole?.currentRole}
                requiredRole={wrongRole?.requiredRole}
            />

            <section className="container-page py-6 sm:py-8" data-testid="transport-section">
                <div className="eyebrow mb-1">Transporte agrícola</div>
                <h2 className="section-title mb-1">Fletes y cargas</h2>
                <p className="text-sm text-muted-foreground mb-4">Encuentra transportistas, solicita un flete o acepta cargas abiertas.</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3">
                    {[
                        {to:"/publicar?tipo=transporte",label:"Solicitar flete",hint:"Indica origen y destino",Icon:Truck},
                        {to:"/solicitudes?tipo=transporte",label:"Cargas disponibles",hint:"Solicitudes abiertas",Icon:Package},
                        {to:"/directorio/transportista",label:"Buscar transportistas",hint:"Directorio de fletes",Icon:MagnifyingGlass},
                        {to:"/ofrecer-transporte",
                            label:"Ofrecer transporte",hint:"Registra tu vehículo",Icon:Storefront},
                    ].map(({to,label,hint,Icon})=><Link key={label} to={to}
                        className="min-w-0 rounded-xl border border-border bg-white p-3 sm:p-4 flex flex-col gap-2">
                        <Icon size={24} className="text-[#2E7D32]" /><strong className="text-sm leading-tight">{label}</strong>
                        <span className="text-xs text-muted-foreground">{hint}</span>
                    </Link>)}
                </div>
                {transportRequests.length > 0 && <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="transport-preview">
                    {transportRequests.map(r=><Link key={r.id} to="/solicitudes?tipo=transporte"
                        className="border border-border rounded-xl p-3 bg-white min-w-0">
                        <strong className="text-sm block truncate">{r.title}</strong>
                        <span className="text-xs text-muted-foreground">{r.city} · {r.quantity} {r.unit}</span>
                    </Link>)}
                </div>}
            </section>
            {/* Categories */}
            <section className="container-page py-6 sm:py-8" data-testid="categories-section" aria-labelledby="home-category-heading">
                <div className="flex items-end justify-between mb-4 sm:mb-8">
                    <div>
                        <div className="eyebrow mb-2">Explora por categoría</div>
                        <h2 className="section-title" id="home-category-heading">¿Qué buscas hoy?</h2>
                        <p className="text-sm text-muted-foreground mt-2">Elige una categoría y encuentra productos para tu negocio.</p>
                    </div>
                </div>
                {loadState.categories === "loading" ? (
                    <p className="text-sm text-muted-foreground" data-testid="categories-loading">
                        Cargando categorías…
                    </p>
                ) : Array.isArray(categories) && categories.length > 0 ? (
                    <div id="home-category-grid" className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
                        {categories.map((c, index) => {
                            const Icon = CAT_ICONS[c.id] || Plant;
                            return (
                                <Link key={c.id} to={`/marketplace?category=${encodeURIComponent(c.id)}`}
                                    aria-label={`Explorar ${c.name}`}
                                    className={`group min-w-0 bg-card text-card-foreground border border-border rounded-2xl p-4 sm:p-5 min-h-[164px] flex flex-col items-start gap-3 hover:border-primary/50 active:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 motion-safe:transition-colors ${!showAllCategories && index >= 6 ? "hidden" : ""}`}
                                    data-testid={`category-${c.id}`}>
                                    <div className="w-full flex items-center justify-between gap-2">
                                        <span className="w-11 h-11 rounded-xl bg-primary/10 text-primary grid place-items-center">
                                            <Icon size={24} weight="duotone" aria-hidden="true" />
                                        </span>
                                        <ArrowRight size={18} className="text-primary" aria-hidden="true" />
                                    </div>
                                    <div className="min-w-0 w-full break-words">
                                        <div className="font-display font-bold text-base leading-snug">{c.name}</div>
                                        <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed mt-1">{CAT_DESCRIPTIONS[c.id] || "Explora los productos de esta categoría"}</p>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                ) : (
                    <p className="text-sm text-muted-foreground" data-testid="categories-empty">
                        {loadState.categories === "loaded"
                            ? "Aún no hay categorías disponibles."
                            : "No pudimos cargar las categorías en este momento."}
                    </p>
                )}
                {categories.length > 6 && (
                    <button type="button" className="mt-4 w-full sm:w-auto px-4 h-11 border border-border rounded-xl font-semibold text-[#2E7D32]"
                        onClick={() => setShowAllCategories((value) => !value)} aria-expanded={showAllCategories} aria-controls="home-category-grid"
                        data-testid="categories-show-more">
                        {showAllCategories ? "Mostrar menos categorías" : `Ver todas las categorías (${categories.length})`}
                    </button>
                )}
            </section>

            <section className="container-page pb-4">
                <Link to="/publicar?tipo=solicitud" className="inline-flex items-center gap-2 text-sm font-semibold text-[#2E7D32]" data-testid="homepage-buy-request-link">
                    <ShoppingCart size={20}/> ¿No encontraste tu producto? Publica lo que buscas gratis <ArrowRight size={16}/>
                </Link>
            </section>
            {/* Urgent buyer requests */}
            {Array.isArray(urgentReqs) && urgentReqs.length > 0 && (
                <section className="bg-[#E8A33A]/8 border-y border-[#E8A33A]/30 py-14 sm:py-16" data-testid="urgent-section">
                    <div className="container-page">
                        <div className="flex items-end justify-between mb-6 flex-wrap gap-2">
                            <div>
                                <div className="eyebrow mb-2 text-[#E8A33A]">⚡ Necesito hoy · Compradores urgentes</div>
                                <h2 className="section-title">Solicitudes urgentes de compra</h2>
                            </div>
                            <Link to="/solicitudes" className="text-[#2E7D32] font-semibold inline-flex items-center gap-1 hover:gap-2" style={{ transition: "gap 180ms ease" }}>
                                Ver todas <ArrowRight size={16} weight="bold" />
                            </Link>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {urgentReqs.map((r) => (
                                <Link key={r.id} to={`/solicitudes`} className="card-lift bg-white border border-border rounded-2xl p-5" data-testid={`urgent-${r.id}`}>
                                    <UrgencyBadge urgency={r.urgency} />
                                    <div className="font-display font-bold text-lg mt-2 line-clamp-2">{r.title}</div>
                                    <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{r.description}</p>
                                    <div className="text-xs text-muted-foreground mt-3 inline-flex items-center gap-1">
                                        <MapPin size={12} /> {r.city}
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Featured / Newest */}
            {Array.isArray(featured) && featured.length > 0 && (
                <ProductSection title="Publicaciones destacadas" eyebrow="Lo más visto del mercado"
                    icon={<Star size={18} weight="fill" />} items={featured} testId="featured-section" />
            )}
            {Array.isArray(newest) && newest.some(item => !featured.some(highlight => highlight.id === item.id)) && (
                <ProductSection title="Recién publicados" eyebrow="Nuevos hoy en CampoDirecto"
                    icon={<Package size={18} weight="fill" />} items={newest.filter(item => !featured.some(highlight => highlight.id === item.id))} testId="newest-section" bg="bg-white" />
            )}

            {SHOW_PREMIUM_MARKET_DATA && <SniimPreviewSection products={pricePromo} status={loadState.marketPrices} />}

            {/* Honest empty state — shown when the marketplace has no real listings yet */}
            {isEmptyMarketplace && (
                <section className="container-page py-14 sm:py-20" data-testid="empty-marketplace-cta">
                    <div className="text-center max-w-2xl mx-auto">
                        <div className="eyebrow mb-2">Bienvenido a CampoDirecto</div>
                        <h2 className="section-title">Aún no hay productos publicados</h2>
                        <p className="text-muted-foreground mt-3 text-lg leading-relaxed">
                            La plataforma acaba de abrir. Sé el primero en publicar y encuentra compradores directos en todo México — publicar es gratis para vendedores.
                        </p>
                        <div className="mt-6 flex gap-3 justify-center flex-wrap">
                            <button
                                type="button"
                                onClick={() => { track("homepage_sell_clicked", {source:"homepage_hero"}); navigate("/publicar?tipo=producto"); }}
                                className="btn-primary"
                                data-testid="empty-publish-cta"
                            >
                                Publicar mi producto
                            </button>
                            <Link
                                to={buildAuthPath({ mode: "register", role: "productor", redirect: "/publicar", action: "publicar" })}
                                className="btn-outline"
                                data-testid="empty-register-cta"
                            >
                                Crear cuenta gratis
                            </Link>
                        </div>
                    </div>
                </section>
            )}

            {/* Verified producers */}
            {Array.isArray(topProducers) && topProducers.length > 0 && (
                <section className="bg-white border-y border-border py-14 sm:py-20" data-testid="verified-producers-section">
                    <div className="container-page">
                        <div className="flex items-end justify-between mb-8 flex-wrap gap-2">
                            <div>
                                <div className="eyebrow mb-2">Confianza del campo</div>
                                <h2 className="section-title">Productores verificados</h2>
                            </div>
                            <Link to="/directorio/productor" className="text-[#2E7D32] font-semibold inline-flex items-center gap-1 hover:gap-2" style={{ transition: "gap 180ms ease" }}>
                                Ver directorio <ArrowRight size={16} weight="bold" />
                            </Link>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                            {topProducers.map((p) => (
                                <div key={p.id} className="card-lift border border-border rounded-2xl overflow-hidden bg-[#F7F7F5]" data-testid={`producer-${p.id}`}>
                                    {p.cover_url && <div className="h-24 bg-cover bg-center" style={{ backgroundImage: `url(${p.cover_url})` }} />}
                                    <div className="p-5">
                                        <div className="flex items-start justify-between gap-3">
                                            <div>
                                                <div className="font-display font-bold text-lg leading-tight">{p.business_name || p.name}</div>
                                                <div className="text-sm text-muted-foreground">{p.city}, {p.state}</div>
                                            </div>
                                            {p.verified && <VerifiedBadge />}
                                        </div>
                                        <p className="text-sm text-muted-foreground mt-3 line-clamp-2">{p.bio}</p>
                                        <div className="mt-4 flex items-center justify-end text-xs">
                                            <Link to={`/perfil/${p.id}`} className="text-[#2E7D32] font-semibold">Ver perfil</Link>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* International disclaimer strip */}
            <section className="bg-secondary/50 border-y border-border" data-testid="disclaimer-section">
                <div className="container-page py-6 flex items-start gap-3 text-sm text-muted-foreground">
                    <Warning size={20} weight="fill" className="text-[#E8A33A] shrink-0 mt-0.5" />
                    <p className="max-w-4xl leading-relaxed">
                        <strong className="text-foreground">Aviso legal:</strong> CampoDirecto facilita el contacto entre compradores, vendedores y transportistas. Cada usuario es responsable de verificar precios, disponibilidad, calidad, permisos, documentación, requisitos aduanales y condiciones comerciales antes de concretar una operación.
                    </p>
                </div>
            </section>
        </>
    );
}

function SniimPreviewSection({ products, status }) {
    return (
        <section className="bg-[#F3F8F1] border-y border-[#2E7D32]/15" data-testid="home-precios-section">
            <div className="container-page py-10 sm:py-12">
                <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
                    <div>
                        <div className="eyebrow mb-2">Datos oficiales SNIIM</div>
                        <h2 className="section-title">Precios oficiales del mercado</h2>
                        <p className="text-muted-foreground mt-2 max-w-2xl">
                            Consulta precios mayoristas recientes publicados por SNIIM.
                        </p>
                    </div>
                    <Link to="/precios-agropecuarios" className="btn-primary min-h-11 px-5 text-sm" data-testid="home-precios-cta">
                        Ver todos los precios <ArrowRight size={16} weight="bold" />
                    </Link>
                </div>

                {status === "loading" ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="home-precios-loading" aria-label="Cargando precios oficiales">
                        {[0, 1, 2, 3].map((item) => <div key={item} className="h-72 rounded-2xl bg-white/70 animate-pulse" />)}
                    </div>
                ) : products.length ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {products.map((product) => {
                            const latest = product.latest;
                            const image = product.image || {};
                            const price = latest.frequent_price ?? latest.min_price ?? latest.max_price;
                            return (
                                <Link key={product.slug} to={`/precios-agropecuarios/${product.slug}`}
                                    className="card-lift overflow-hidden bg-white border border-border rounded-2xl min-w-0"
                                    aria-label={`Consultar precio SNIIM de ${product.display_name}`}
                                    data-testid={`home-precio-${product.slug}`}>
                                    <div className="aspect-[16/9] bg-[#E7EEE3] overflow-hidden">
                                        {image.status === "approved" && image.url ? (
                                            <img src={image.url} alt={image.alt_text || product.display_name}
                                                width="480" height="270" loading="lazy" decoding="async"
                                                className="w-full h-full object-cover" />
                                        ) : (
                                            <div className="w-full h-full grid place-items-center text-sm text-muted-foreground">Foto pendiente</div>
                                        )}
                                    </div>
                                    <div className="p-4">
                                        <div className="text-xs text-[#2E7D32] uppercase tracking-wider font-bold">SNIIM · {product.category}</div>
                                        <h3 className="font-display font-bold text-lg leading-tight mt-1 line-clamp-2">{product.display_name}</h3>
                                        <div className="mt-3 text-2xl font-extrabold tracking-tight">
                                            {Number.isFinite(Number(price)) ? `MX$${Number(price).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "Precio por actualizar"}
                                            {latest.unit && <span className="text-xs text-muted-foreground font-medium"> /{latest.unit}</span>}
                                        </div>
                                        <p className="text-xs text-muted-foreground truncate mt-1" title={latest.presentation}>{latest.presentation || "Presentación oficial"}</p>
                                        <p className="text-xs text-muted-foreground mt-2">Observado: {latest.observation_date || latest.date}</p>
                                        <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{latest.source || "SNIIM — Secretaría de Economía"}</p>
                                        <span className="inline-flex mt-3 rounded-full bg-[#2E7D32]/10 px-2.5 py-1 text-xs font-semibold text-[#2E7D32]">
                                            {product.status_label || "Último precio oficial disponible"}
                                        </span>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                ) : (
                    <div className="rounded-2xl border border-border bg-white p-6 text-sm text-muted-foreground" data-testid="home-precios-empty">
                        {status === "unavailable" ? "No pudimos cargar los precios oficiales en este momento." : "SNIIM aún no tiene precios públicos disponibles."}
                    </div>
                )}

                <div className="mt-6 flex items-center justify-between gap-4 rounded-xl border border-border bg-white px-4 py-3 flex-wrap" data-testid="home-profeco-link">
                    <div>
                        <div className="font-semibold">Precios al consumidor — PROFECO</div>
                        <div className="text-sm text-muted-foreground">Consulta el último periodo oficial disponible de precios minoristas.</div>
                    </div>
                    <Link to="/precios-profeco" className="text-[#2E7D32] font-semibold inline-flex items-center gap-1 min-h-11 px-2">
                        Consultar PROFECO <ArrowRight size={16} weight="bold" />
                    </Link>
                </div>
            </div>
        </section>
    );
}

function ProductSection({ title, eyebrow, icon, items, testId, bg }) {
    if (!Array.isArray(items) || items.length === 0) return null;
    return (
        <section className={`${bg || ""} py-6 sm:py-14`} data-testid={testId}>
            <div className="container-page">
                <div className="flex items-end justify-between mb-8 flex-wrap gap-2">
                    <div>
                        <div className="eyebrow mb-2 inline-flex items-center gap-2">{icon} {eyebrow}</div>
                        <h2 className="section-title">{title}</h2>
                    </div>
                    <Link to="/marketplace" className="text-[#2E7D32] font-semibold inline-flex items-center gap-1 hover:gap-2" style={{ transition: "gap 180ms ease" }}>
                        Ver todo <ArrowRight size={16} weight="bold" />
                    </Link>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {items.slice(0, 8).map((p) => <ProductCard key={p.id} item={p} />)}
                </div>
            </div>
        </section>
    );
}
